import { getFunctions, httpsCallable } from 'firebase/functions';
import { getAuth } from 'firebase/auth';

export interface ChatMessage {
  role: 'user' | 'assistant';
  content: string;
}

export interface AssistantAction {
  type: 'navigate';
  data?: { path?: string; label?: string };
}

interface AssistantRequest {
  message: string;
  conversationHistory?: ChatMessage[];
  userName?: string;
  userRole?: string;
}

interface AssistantResponse {
  success: boolean;
  response?: string;
  actions?: AssistantAction[];
  error?: string;
}

export interface AssistantReply {
  response: string;
  actions: AssistantAction[];
}

/**
 * Calls the `assistant` Cloud Function (VERA's in-app assistant). The
 * Anthropic key lives only in the function; this just relays the message +
 * short history and returns the reply plus any navigation actions.
 */
class AssistantService {
  async ask(
    message: string,
    conversationHistory: ChatMessage[],
    userName?: string,
    userRole?: string
  ): Promise<AssistantReply> {
    const auth = getAuth();
    if (!auth.currentUser) {
      throw new Error('You must be logged in to use the assistant');
    }

    const functions = getFunctions();
    const callable = httpsCallable<AssistantRequest, AssistantResponse>(functions, 'assistant', {
      timeout: 30000,
    });

    const res = await callable({ message, conversationHistory, userName, userRole });
    if (!res.data.success || !res.data.response) {
      throw new Error(res.data.error || 'Assistant failed');
    }
    return { response: res.data.response, actions: res.data.actions || [] };
  }
}

export const assistantService = new AssistantService();
